// Manual component groups config — defines how components are grouped in the library view.
// Group membership is based on registry paths (unique, unlike names).

export interface ComponentCluster {
  label: string
  componentPaths: string[]
}

export interface ComponentGroup {
  label: string
  description: string
  componentPaths: string[]
  clusters?: ComponentCluster[]
}

export const componentGroups: ComponentGroup[] = [
  {
    label: 'Buttons',
    description: 'Click actions — from primitives to composite action sets',
    componentPaths: [
      'components/FbUI/FbButton.tsx',
      'components/UI/VariantSelectButton',
      'components/Buttons',
    ],
  },
  {
    label: 'Checkboxes',
    description: 'Single-item and multi-item selection controls',
    componentPaths: ['components/Checkbox'],
  },
  {
    label: 'Select & Dropdown',
    description: 'Choose-from-list controls across complexity levels',
    componentPaths: [
      'components/UI/Select',
      'components/UI/BaseDropdown',
      'components/UI/Dropdown',
      'components/UI/combobox',
      'components/SortSelect',
      'components/CreatableTagSelector',
    ],
  },
  {
    label: 'Tabs',
    description: 'Horizontal navigation between content panels',
    componentPaths: [],
    clusters: [
      {
        label: 'components/Tabs',
        componentPaths: ['components/Tabs'],
      },
      {
        label: 'components/FbUI/Tabs',
        componentPaths: ['components/FbUI/Tabs.tsx'],
      },
    ],
  },
  {
    label: 'Modals & Overlays',
    description: 'Dialogs, popovers, and confirmation prompts',
    componentPaths: [
      'components/UI/Modal',
      'components/UI/popover.tsx',
      'components/dialogConfirm.tsx',
    ],
  },
  {
    label: 'Text & Links',
    description: 'Typography, truncation, tooltips, and navigation links',
    componentPaths: [
      'components/OverflownText.tsx',
      'components/UI/Anchor.tsx',
      'components/FbUI/FbLink',
    ],
  },
  {
    label: 'Status Indicators',
    description: 'Pills, badges, and banners that communicate state',
    componentPaths: [
      'components/FbUI/TextPill.tsx',
      'components/FbUI/EmailValidityPill.tsx',
      'components/FbUI/TagCountBadge.tsx',
      'components/FbUI/JobStatusPill.tsx',
      'components/FbUI/StagePill.tsx',
      'components/PredictedTrafficBadge',
      'components/Banners',
    ],
  },
  {
    label: 'Form & Input',
    description: 'Data-entry controls and form utilities',
    componentPaths: [
      'components/UI/Switch',
      'components/UI/calendar.tsx',
      'components/UI/command.tsx',
      'components/FileDragDrop.tsx',
      'components/ColorPicker.tsx',
    ],
    clusters: [
      {
        label: 'FormUtilsV2',
        componentPaths: [
          'components/FormUtilsV2/Input',
          'components/FormUtilsV2/Label',
          'components/FormUtilsV2/TextInput',
        ],
      },
      {
        label: 'FormUtils',
        componentPaths: [
          'components/FormUtils',
          'components/FormUtils/DatePicker.tsx',
          'components/FormUtils/DateRangePicker.tsx',
        ],
      },
    ],
  },
  {
    label: 'Tables & Filters',
    description: 'Data grid and filter chrome for data views',
    componentPaths: ['components/ClientDataTable', 'components/Filters'],
  },
  {
    label: 'Layout',
    description: 'Page structure, containers, and info panels',
    componentPaths: [
      'components/Layout',
      'components/UI/InfoCard.tsx',
      'components/FbUI/Page',
    ],
  },
  {
    label: 'Tree & Lists',
    description: 'Hierarchical data and structured list rendering',
    componentPaths: [
      'components/UI/TreeList.tsx',
      'components/Icons',
      'components/json-into-ul.tsx',
    ],
  },
  {
    label: 'Files & Attachments',
    description: 'File display, drag-and-drop upload, and attachment lists',
    componentPaths: [
      'components/UI/Attachment',
      'components/Attachments',
      'components/FileContainer',
    ],
  },
  {
    label: 'Utilities',
    description: 'Error states, tooltips, carousels, and misc helpers',
    componentPaths: [
      'components/UI/ErrorBoundary.tsx',
      'components/NotFoundCard',
      'components/NotAuthorizedCard',
      'components/Tooltip',
      'components/UI/Carousel',
      'components/Editor',
    ],
  },
]

/**
 * Components that need full-width display in the grid.
 * These span the entire row so their preview has enough horizontal room.
 */
export const wideComponents: Set<string> = new Set([
  'ClientDataTable',
  'Filters',
  'Banners',
  'Editor',
  'Tabs',
])

/** Components excluded from the library view — DataTable-like, mobile-only, or domain-specific. */
export const excludedPaths: Set<string> = new Set([
  // DataTable variants — show only ClientDataTable in catalog; DataTable needs real backend
  'components/DataTable',
  'components/Tables',
  'components/DocumentLibrary',
  // Mobile-only components
  'components/MobileDrawer',
  'components/MobileDropdown',
  'components/MobileConfirmationModal',
  'components/MobilePreview.tsx',
  'components/MobileInfiniteScrollView',
  'components/MobileListViewHeader.tsx',
  'components/MobileSearchSelectView',
  'components/Mobile',
  // Domain-specific / app-wired
  'components/Modals',
  'components/NewCompanyPage',
  'components/MobileCompanyPage',
  'components/Dashboard',
  'components/Charts',
  'components/SalesStage',
  'components/Opportunities',
  'components/Reminders',
  'components/Samples',
  'components/Notes',
  'components/UI/ProductSearchModal',
  'components/UI/BrandSelector.tsx',
  'components/UI/ProductVariantSelector',
  'components/UI/SingleVariantPicker',
  'components/UI/ProductSelector.tsx',
  'components/UnifiedSearch',
  'components/change-product-view-modal.tsx',
  'components/CompanyAutocomplete',
  'components/CreatableCampaignSelector',
  'components/CreatableDistributorSelector',
  'components/CreatableGPOSelector',
  'components/Actions',
  'components/Cards',
  'components/ContactCard',
  'components/ContactRequestCard',
  'components/Notification',
  'components/ProductFlow',
  'components/RecommendationsCard',
  'components/offer-page',
  'components/Credits',
  'components/PerformanceDashboard',
  'components/ContactStarToggle.tsx',
  'components/EntityDetailPage',
  'components/MobileSearch',
  'components/MobileFilterCriteriaSelect',
  'components/MobileFiltersModal',
])
